import React, { useMemo, useState } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { normalizeTimestamp, formatValue } from './chartUtils';
import WidgetState from './WidgetState';

const HeatmapWidget = ({ data, config = {} }) => {
  const [hoveredCell, setHoveredCell] = useState(null);

  // Memoize the expensive data processing
  const processedData = useMemo(() => {
    if (!data || !data.data || data.type !== 'timeseries') {
      return null;
    }
    return processTimeseriesForHeatmap(data.data);
  }, [data]);

  // Memoize cell dimensions
  const cellDimensions = useMemo(() => {
    if (!processedData) return { cellWidth: 0, cellHeight: 0 };
    return {
      cellWidth: processedData.columns.length > 0 ? 100 / processedData.columns.length : 0,
      cellHeight: processedData.rows.length > 0 ? 100 / processedData.rows.length : 0,
    };
  }, [processedData]);

  // Pre-compute all cell colors to avoid per-cell calculation in render
  const cellColors = useMemo(() => {
    if (!processedData) return [];
    return processedData.values.map(cell =>
      // A null cell means "no sample", which is visually distinct from a real
      // low value — render it as a neutral gap rather than a scale colour.
      cell.value === null
        ? 'rgba(255,255,255,0.06)'
        : getHeatmapColor(cell.value, processedData.min, processedData.max)
    );
  }, [processedData]);

  if (!processedData || processedData.values.length === 0) {
    return (
      <WidgetState isEmpty />
    );
  }

  const { cellWidth, cellHeight } = cellDimensions;

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* Y-axis labels */}
      <Box sx={{ position: 'absolute', left: 0, top: 0, width: '60px', height: '100%' }}>
        {processedData.rows.map((row, rowIndex) => (
          <Box
            key={`y-${rowIndex}`}
            sx={{
              position: 'absolute',
              top: `${rowIndex * cellHeight}%`,
              height: `${cellHeight}%`,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              pr: 0.5,
              fontSize: '12px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            <Typography variant="caption" noWrap>{row}</Typography>
          </Box>
        ))}
      </Box>

      {/* X-axis labels */}
      <Box sx={{ position: 'absolute', left: '60px', bottom: 0, width: 'calc(100% - 60px)', height: '20px' }}>
        {processedData.columns.map((column, colIndex) => (
          <Box
            key={`x-${colIndex}`}
            sx={{
              position: 'absolute',
              left: `${colIndex * cellWidth}%`,
              width: `${cellWidth}%`,
              height: '100%',
              textAlign: 'center',
              fontSize: '10px',
              overflow: 'hidden',
            }}
          >
            <Typography variant="caption" noWrap sx={{ fontSize: '10px' }}>{column}</Typography>
          </Box>
        ))}
      </Box>

      {/* Heatmap cells */}
      <Box sx={{ position: 'absolute', left: '60px', top: 0, width: 'calc(100% - 60px)', height: 'calc(100% - 20px)' }}>
        {processedData.values.map((cell, cellIndex) => {
          const rowIndex = Math.floor(cellIndex / processedData.columns.length);
          const colIndex = cellIndex % processedData.columns.length;

          return (
            <Tooltip
              key={cellIndex}
              title={`${processedData.rows[rowIndex]}, ${processedData.columns[colIndex]}: ${
                cell.value === null ? 'No data' : formatValue(cell.value, config)
              }`}
              arrow
              placement="top"
            >
              <Box
                // Cell detail lived in a hover tooltip only; focus mirrors it so
                // the grid is traversable by keyboard.
                tabIndex={0}
                role="img"
                aria-label={`${processedData.rows[rowIndex]}, ${processedData.columns[colIndex]}: ${
                  cell.value === null ? 'No data' : formatValue(cell.value, config)
                }`}
                onFocus={() => setHoveredCell(cellIndex)}
                onBlur={() => setHoveredCell(null)}
                onMouseEnter={() => setHoveredCell(cellIndex)}
                onMouseLeave={() => setHoveredCell(null)}
                sx={{
                  position: 'absolute',
                  left: `${colIndex * cellWidth}%`,
                  top: `${rowIndex * cellHeight}%`,
                  width: `${cellWidth}%`,
                  height: `${cellHeight}%`,
                  backgroundColor: cellColors[cellIndex],
                  border: '1px solid',
                  borderColor: hoveredCell === cellIndex ? 'common.white' : 'rgba(255,255,255,0.3)',
                  cursor: 'pointer',
                  opacity: hoveredCell === cellIndex ? 1 : 0.9,
                  transition: 'opacity 0.15s',
                }}
              />
            </Tooltip>
          );
        })}
      </Box>

      {/* Color legend */}
      <Box sx={{ position: 'absolute', right: 0, top: 0, width: '20px', height: '100px' }}>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '20px',
            height: '100px',
            background: 'linear-gradient(to bottom, #ff4d4f, #ffa940, #52c41a)',
            borderRadius: 0.5,
          }}
        />
        <Typography variant="caption" sx={{ position: 'absolute', top: 0, right: '25px', fontSize: '11px' }}>
          {formatValue(processedData.max, config)}
        </Typography>
        <Typography variant="caption" sx={{ position: 'absolute', top: '45px', right: '25px', fontSize: '11px' }}>
          {formatValue((processedData.max + processedData.min) / 2, config)}
        </Typography>
        <Typography variant="caption" sx={{ position: 'absolute', top: '88px', right: '25px', fontSize: '11px' }}>
          {formatValue(processedData.min, config)}
        </Typography>
      </Box>
    </Box>
  );
};

// Process timeseries data for heatmap visualization
// Optimized: uses Map for O(1) lookups instead of Array.find O(n)
function processTimeseriesForHeatmap(timeseriesData) {
  const timestamps = new Set();
  // Build value lookup maps per series for O(1) access. Normalize to epoch ms
  // BEFORE dedup so seconds/millis mixes collapse to one key and both the sort
  // and the per-cell lookups agree on the key.
  const seriesLookups = timeseriesData.map(series => {
    const lookup = new Map();
    series.values.forEach(point => {
      const ts = normalizeTimestamp(point[0]);
      timestamps.add(ts);
      const val = parseFloat(point[1]);
      lookup.set(ts, isNaN(val) ? 0 : val);
    });
    return lookup;
  });

  // Numeric sort — lexicographic sort misorders timestamps of differing digit counts.
  const sortedTimestamps = Array.from(timestamps).sort((a, b) => a - b);

  const rows = timeseriesData.map(series => {
    if (series.metric) {
      const labels = Object.entries(series.metric)
        .filter(([key]) => !key.startsWith('__'))
        .map(([key, value]) => `${key}="${value}"`)
        .join(', ');

      if (labels) return labels;
      if (series.metric.__name__) return series.metric.__name__;
    }
    return 'Series';
  });

  const columns = sortedTimestamps.map(timestamp =>
    new Date(timestamp).toLocaleTimeString()
  );

  const values = [];
  let min = Infinity;
  let max = -Infinity;

  rows.forEach((row, rowIndex) => {
    const lookup = seriesLookups[rowIndex];
    sortedTimestamps.forEach(timestamp => {
      // A MISSING sample is not a zero. Coercing it to 0 painted a real
      // "healthy" cell for absent data AND dragged the min/max colour scale
      // toward zero, distorting every other cell. Keep it null and let the
      // renderer show a gap. (TableWidget/TimeSeriesTable already emit null.)
      const raw = lookup.get(timestamp);
      const value = raw === undefined ? null : raw;
      values.push({ rowIndex, timestamp, value });
      if (value !== null) {
        if (value < min) min = value;
        if (value > max) max = value;
      }
    });
  });

  return { rows, columns, values, min, max };
}

function getHeatmapColor(value, min, max) {
  const range = max - min;
  if (range === 0) return 'rgb(82, 196, 26)';
  const normalized = Math.max(0, Math.min(1, (value - min) / range));

  if (normalized <= 0.5) {
    const r = Math.round(255 * (normalized * 2));
    return `rgb(${r}, 196, 26)`;
  } else {
    const g = Math.round(255 * (1 - (normalized - 0.5) * 2));
    return `rgb(255, ${g}, 26)`;
  }
}

export default React.memo(HeatmapWidget, (prevProps, nextProps) => {
  return (
    prevProps.data === nextProps.data &&
    prevProps.config?.unit === nextProps.config?.unit &&
    prevProps.config?.decimalPlaces === nextProps.config?.decimalPlaces &&
    prevProps.config?.useThousandSeparator === nextProps.config?.useThousandSeparator
  );
});
